/**
 * CollaborationSection Component
 *
 * Container for the Collaboration section of the Workspace Overview.
 * Two-panel layout:
 * - Left Panel (~30%): YOUR CHANNELS - Expandable teams with nested channels
 * - Right Panel (~70%): ACTIVITY - Three columns (Shared With You, File Activity, New Members)
 *
 * Part of the "Command Docket" aesthetic - editorial legal meets mission control.
 */

import { makeStyles, shorthands } from '@fluentui/react-components';
import { YourChannelsPanel } from './YourChannelsPanel';
import { ActivityPanel } from './ActivityPanel';

interface CollaborationSectionProps {
  tenantId: string;
}

const useStyles = makeStyles({
  // Section container
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginTop: '28px',
  },

  // Editorial divider with centered label
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.margin('8px', '0', '8px', '0'),
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 100%)',
  },

  dividerLineRight: {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.12) 100%)',
  },

  dividerLabel: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.3)',
    flexShrink: 0,
  },

  // Two-panel grid layout
  grid: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '24px',
    minHeight: '300px',
  },

  // Left panel - Channels
  channelsPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('16px'),
  },

  // Right panel - Activity
  activityPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
    ...shorthands.padding('16px'),
  },

  // Activity columns grid
  activityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    flex: 1,
  },

  // Individual activity column
  activityColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  // Column header
  columnHeader: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.4)',
    ...shorthands.padding('0', '0', '8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', 'rgba(0, 0, 0, 0.06)'),
  },

  // Column content placeholder
  columnContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});

export function CollaborationSection({ tenantId }: CollaborationSectionProps) {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      {/* Editorial Section Divider */}
      <div className={styles.dividerContainer}>
        <div className={styles.dividerLine} />
        <span className={styles.dividerLabel}>Collaboration</span>
        <div className={`${styles.dividerLine} ${styles.dividerLineRight}`} />
      </div>

      {/* Two-Panel Grid */}
      <div className={styles.grid} data-testid="collaboration-grid">
        {/* Left Panel - YOUR CHANNELS */}
        <div className={styles.channelsPanel} data-testid="channels-panel">
          <YourChannelsPanel tenantId={tenantId} />
        </div>

        {/* Right Panel - ACTIVITY */}
        <div className={styles.activityPanel} data-testid="activity-panel-wrapper">
          <ActivityPanel tenantId={tenantId} />
        </div>
      </div>
    </div>
  );
}
